import { User, Building2 } from 'lucide-react';
import { generateInitials, generateAvatarColor } from '../../utils/thumbnailUtils';

interface AvatarProps {
  src?: string | null;
  name: string;
  type?: 'user' | 'company';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showFallback?: boolean;
}

const Avatar = ({ 
  src, 
  name, 
  type = 'user', 
  size = 'md', 
  className = '',
  showFallback = true 
}: AvatarProps) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-24 h-24 text-xl'
  };

  const iconSizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };

  const isUser = type === 'user';
  const Icon = isUser ? User : Building2;
  const initials = generateInitials(name);
  const backgroundColor = generateAvatarColor(name);

  const baseClasses = `inline-flex items-center justify-center font-medium text-white ${
    isUser ? 'rounded-full' : 'rounded-lg'
  } ${sizeClasses[size]} ${className}`;

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`${baseClasses} object-cover`}
        onError={(e) => {
          // Hide image on error and show fallback
          e.currentTarget.style.display = 'none';
          if (showFallback && e.currentTarget.nextSibling) {
            (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
          }
        }}
      />
    );
  }

  if (!showFallback) {
    return null;
  }

  return (
    <div
      className={baseClasses}
      style={{ backgroundColor }}
    >
      {name ? (
        <span className="font-semibold">{initials}</span>
      ) : (
        <Icon className={iconSizes[size]} />
      )}
    </div>
  );
};

export default Avatar;